import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  TrendingUp, 
  ArrowUp, 
  ArrowRight, 
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  Check,
  RotateCcw,
  Link2Off,
  Hammer,
  Shield,
  Coins,
  Unlock,
  Crown,
  Sparkles,
  Target,
  BookOpen,
  Zap,
  AlertTriangle,
  Lightbulb,
  Plus
} from 'lucide-react';
import LadderOfWealthChart from './LadderOfWealthChart';

// =============================================================================
// WEALTH STAGES - DO NOT MODIFY
// =============================================================================
const WEALTH_STAGES = [
  {
    key: 'supported',
    name: 'Supported',
    nameEs: 'Dependiente',
    ladderName: 'Dependence',
    ladderNameEs: 'Dependencia',
    level: 1,
    description: 'Your financial foundation depends on external support. This is the starting point — building independent income is the first priority.',
    descriptionEs: 'Tu base financiera depende de apoyo externo. Este es el punto de partida — construir ingresos independientes es la primera prioridad.',
    color: '#DC2626',
    icon: Link2Off
  },
  {
    key: 'builder',
    name: 'Builder', 
    nameEs: 'Constructor',
    ladderName: 'Productivity',
    ladderNameEs: 'Productividad',
    level: 2,
    description: 'You generate income but lack financial cushion. Builders are actively working toward stability, but resilience remains fragile.',
    descriptionEs: 'Generas ingresos pero careces de colchón financiero. Los constructores trabajan activamente hacia la estabilidad, pero la resiliencia sigue siendo frágil.',
    color: '#EA580C',
    icon: Hammer
  },
  {
    key: 'stabilizer',
    name: 'Stabilizer',
    nameEs: 'Estabilizador',
    ladderName: 'Independence',
    ladderNameEs: 'Independencia',
    level: 3,
    description: 'You have built a buffer against uncertainty. Stabilizers can weather short-term disruptions and are positioned to begin accumulating.',
    descriptionEs: 'Has construido un colchón contra la incertidumbre. Los estabilizadores pueden resistir disrupciones a corto plazo y están posicionados para comenzar a acumular.',
    color: '#F59E0B',
    icon: Shield
  },
  {
    key: 'accumulator',
    name: 'Accumulator',
    nameEs: 'Acumulador',
    ladderName: 'Stability',
    ladderNameEs: 'Estabilidad',
    level: 4,
    description: 'You are actively building assets and creating partial financial autonomy. The transition from earned income to investment income is underway.',
    descriptionEs: 'Estás construyendo activos activamente y creando autonomía financiera parcial. La transición de ingresos laborales a ingresos de inversión está en marcha.',
    color: '#84CC16',
    icon: Coins
  },
  {
    key: 'investor',
    name: 'Investor',
    nameEs: 'Inversor',
    ladderName: 'Security',
    ladderNameEs: 'Seguridad',
    level: 5,
    description: 'Your investments generate meaningful returns. Passive income streams are reducing your dependency on active work.',
    descriptionEs: 'Tus inversiones generan retornos significativos. Los flujos de ingresos pasivos están reduciendo tu dependencia del trabajo activo.',
    color: '#059669',
    icon: TrendingUp
  },
  {
    key: 'lifeOwner',
    name: 'Life Owner',
    nameEs: 'Dueño de tu Vida',
    ladderName: 'Freedom',
    ladderNameEs: 'Libertad',
    level: 6,
    description: 'Your passive income fully covers your lifestyle. Time is your own. Financial decisions are now about optimization, not survival.',
    descriptionEs: 'Tus ingresos pasivos cubren completamente tu estilo de vida. El tiempo es tuyo. Las decisiones financieras son ahora sobre optimización, no supervivencia.',
    color: '#1E3A8A',
    icon: Unlock
  },
  {
    key: 'legacyDesigner',
    name: 'Legacy Designer',
    nameEs: 'Diseñador de Legado',
    ladderName: 'Abundance',
    ladderNameEs: 'Abundancia',
    level: 7,
    description: 'Your wealth exceeds your needs. You are designing impact, legacy, and meaning beyond personal finance.',
    descriptionEs: 'Tu riqueza supera tus necesidades. Estás diseñando impacto, legado y significado más allá de las finanzas personales.',
    color: '#8B5CF6',
    icon: Crown
  }
];

// =============================================================================
// DIRECTIONS - DO NOT MODIFY
// =============================================================================
const DIRECTIONS = {
  advancing: {
    key: 'advancing',
    label: 'Advancing',
    icon: ArrowUp,
    color: '#10B981',
    description: 'Your income and resilience are strengthening. You are moving toward the next stage.'
  },
  steady: {
    key: 'steady',
    label: 'Steady',
    icon: ArrowRight,
    color: '#F59E0B',
    description: 'Your position is stable. Focus on building momentum to advance.'
  },
  contracting: {
    key: 'contracting',
    label: 'Contracting',
    icon: ArrowDown,
    color: '#EF4444',
    description: 'Your financial position is under pressure. Stabilizing should be the immediate priority.'
  }
};

// =============================================================================
// QUIZ QUESTIONS - DO NOT MODIFY
// =============================================================================
const QUESTIONS = [
  {
    id: 1,
    question: 'If you stopped working today, how would you pay for your current lifestyle?',
    questionEs: 'Si dejaras de trabajar hoy, ¿cómo pagarías tu estilo de vida actual?',
    options: [
      { id: 'depend', label: "I wouldn't be able to, I would depend on others or on debt", labelEs: "No podría, dependería de otros o de deudas", value: 1 },
      { id: 'savings', label: 'My savings would cover me temporarily', labelEs: 'Mis ahorros me cubrirían temporalmente', value: 2 },
      { id: 'partial', label: 'My investments or business income would allow me to survive, but I would have to cut some expenses', labelEs: 'Mis inversiones o ingresos de negocio me permitirían sobrevivir, pero tendría que recortar gastos', value: 3 },
      { id: 'full', label: 'My investments could fully cover my lifestyle', labelEs: 'Mis inversiones podrían cubrir completamente mi estilo de vida', value: 4 },
      { id: 'surplus', label: "My investment income far exceeds my lifestyle spending, it wouldn't be a problem", labelEs: "Mis ingresos por inversiones superan con creces mis gastos, no sería un problema", value: 5 }
    ]
  },
  {
    id: 2,
    question: 'Right now, does the money you earn from work cover your monthly expenses?',
    questionEs: '¿Actualmente, el dinero que ganas de tu trabajo cubre tus gastos mensuales?',
    options: [
      { id: 'no', label: 'No', labelEs: 'No', value: 1 },
      { id: 'barely', label: 'Barely', labelEs: 'Apenas', value: 2 },
      { id: 'yes', label: 'Yes', labelEs: 'Sí', value: 3 },
      { id: 'surplus', label: 'Yes, with money left over', labelEs: 'Sí, con dinero de sobra', value: 4 }
    ]
  },
  {
    id: 3,
    question: 'If all income stopped, how long could you maintain your current lifestyle?',
    questionEs: 'Si todos tus ingresos se detuvieran, ¿cuánto tiempo podrías mantener tu estilo de vida actual?',
    options: [
      { id: 'less_1', label: 'Less than 1 month', labelEs: 'Menos de 1 mes', value: 1 },
      { id: '1_3', label: '1–3 months', labelEs: '1–3 meses', value: 2 },
      { id: '3_12', label: '3–12 months', labelEs: '3–12 meses', value: 3 },
      { id: 'more_1yr', label: 'More than 1 year', labelEs: 'Más de 1 año', value: 4 },
      { id: 'indefinite', label: 'Indefinitely', labelEs: 'Indefinidamente', value: 5 }
    ]
  }
];

// =============================================================================
// SCORING LOGIC - DO NOT MODIFY
// =============================================================================
const calculateStage = (q1, q2, q3) => {
  if (q1 === 1) return 'supported';
  if (q1 === 2) {
    if ((q2 === 1 || q2 === 2) && q3 <= 2) return 'builder';
    return 'stabilizer';
  }
  if (q1 === 3) {
    if ((q2 === 3 || q2 === 4) && q3 >= 3) return 'investor';
    return 'accumulator';
  }
  if (q1 === 4) return 'lifeOwner';
  if (q1 === 5) return 'legacyDesigner';
  return 'builder';
};

const calculateDirection = (q2, q3) => {
  if ((q2 === 3 || q2 === 4) && q3 >= 3) return 'advancing';
  if (q2 === 2 || q3 === 2) return 'steady';
  if (q2 === 1 || q3 === 1) return 'contracting';
  return 'steady';
};

// =============================================================================
// CONTENT TRANSLATIONS
// =============================================================================
const content = {
  en: {
    intro: {
      title: "The Wealth Ladder",
      subtitle: "Understand where you stand on your financial journey",
      description: "In the next few minutes, you'll discover your current position on the ProsperMind Wealth Ladder and understand what it takes to climb to the next level.",
      time: "6-8 min",
      button: "Begin Journey"
    },
    concept: {
      title: "The 7 Stages of Wealth",
      intro: "Financial freedom isn't a single destination — it's a ladder with seven distinct stages. Each stage represents a different relationship with money.",
      insight: "Most people never consciously identify where they stand. Without this awareness, progress becomes random rather than intentional.",
      button: "Explore the Stages"
    },
    stageIntro: {
      title: "Let's Explore Each Stage",
      description: "Understanding each stage helps you see the path ahead. As we go through them, think about which one resonates most with your current situation.",
      button: "Start with Stage 1"
    },
    stages: {
      1: {
        title: "Stage 1: Dependence",
        explanation: "At this stage, someone else provides your financial resources. You may rely on family, a partner, government assistance, or debt to cover your basic needs.",
        objective: "Develop marketable skills and start generating your own income. The goal is to create value that others will pay for.",
        insight: "This is where everyone starts. There's no shame in being here — only in staying here without a plan."
      },
      2: {
        title: "Stage 2: Productivity",
        explanation: "You now generate income through your skills and effort, but your earnings don't fully cover your expenses. You're working, but still struggling to make ends meet.",
        objective: "Increase your income and/or reduce expenses until your earnings consistently exceed your costs.",
        insight: "Many people get stuck here for years. The key is finding ways to increase the value you create, not just working more hours."
      },
      3: {
        title: "Stage 3: Independence",
        explanation: "Your income now covers your expenses, and sometimes you can save money. You're no longer dependent on others, but you haven't built significant reserves yet.",
        objective: "Build consistent savings habits and create an emergency fund that gives you breathing room.",
        insight: "This is a critical turning point. From here, your choices determine whether you climb or stay stuck."
      },
      4: {
        title: "Stage 4: Stability",
        explanation: "You generate savings consistently and may have started earning extra income from those savings through interest or small investments.",
        objective: "Grow your investments and begin creating passive income streams that don't require your direct time.",
        insight: "At this stage, your money starts working for you. The compound effect begins to accelerate your progress."
      },
      5: {
        title: "Stage 5: Security",
        explanation: "Income from your investments now covers your basic expenses. You have diversified assets and no expensive debt. Work becomes a choice, not a necessity for survival.",
        objective: "Expand your investment income until it covers not just basics, but your entire desired lifestyle.",
        insight: "You've achieved what most people only dream of — true financial security. But there's still more ladder to climb."
      },
      6: {
        title: "Stage 6: Financial Freedom",
        explanation: "Your investment income fully covers your lifestyle with room to spare. You don't need to work for money anymore. Your time is truly your own.",
        objective: "Optimize your portfolio for long-term sustainability and begin thinking about impact beyond personal wealth.",
        insight: "Freedom isn't about having unlimited money — it's about having enough that money no longer dictates your choices."
      },
      7: {
        title: "Stage 7: Abundance",
        explanation: "Your wealth far exceeds your needs. Money is no longer a concern. You're now focused on legacy, impact, and meaning beyond personal finance.",
        objective: "Design how your wealth will create lasting positive impact for others and future generations.",
        insight: "The final stage isn't about more money — it's about using your resources to create something that outlasts you."
      }
    },
    ladderRecap: {
      title: "The Complete Wealth Ladder",
      subtitle: "You've learned all 7 stages. Here's the full picture:",
      insight: "Most people spend their entire lives without knowing which stage they're in. You're about to find out exactly where you stand.",
      button: "Find My Stage"
    },
    questionIntro: {
      title: "Now Let's Find Your Stage",
      description: "Answer three quick questions honestly. There are no right or wrong answers — only your current reality.",
      button: "Start Assessment"
    },
    analyzing: {
      phrases: [
        'Evaluating autonomy...',
        'Measuring resilience...',
        'Calculating trajectory...',
        'Positioning on ProsperMind Ladder...'
      ]
    },
    result: {
      title: "Your stage in the ladder is ready!",
      currentStage: "Your Current Stage",
      nextStage: "Next Stage",
      direction: "Your Direction",
      directionLabels: {
        advancing: "Advancing",
        steady: "Steady",
        contracting: "Going Down"
      },
      retakeButton: "Retake Assessment",
      finishButton: "Finish"
    }
  },
  es: {
    intro: {
      title: "La Escalera de la Riqueza",
      subtitle: "Entiende dónde te encuentras en tu viaje financiero",
      description: "En los próximos minutos, descubrirás tu posición actual en la Escalera de Riqueza de ProsperMind y entenderás qué se necesita para subir al siguiente nivel.",
      time: "6-8 min",
      button: "Comenzar Viaje"
    },
    concept: {
      title: "Las 7 Etapas de la Riqueza",
      intro: "La libertad financiera no es un destino único — es una escalera con siete etapas distintas. Cada etapa representa una relación diferente con el dinero.",
      insight: "La mayoría de las personas nunca identifican conscientemente dónde se encuentran. Sin esta conciencia, el progreso se vuelve aleatorio en lugar de intencional.",
      button: "Explorar las Etapas"
    },
    stageIntro: {
      title: "Exploremos Cada Etapa",
      description: "Entender cada etapa te ayuda a ver el camino por delante. Mientras las revisamos, piensa cuál resuena más con tu situación actual.",
      button: "Comenzar con Etapa 1"
    },
    stages: {
      1: {
        title: "Etapa 1: Dependencia",
        explanation: "En esta etapa, alguien más provee tus recursos financieros. Puedes depender de familia, pareja, asistencia gubernamental o deudas para cubrir tus necesidades básicas.",
        objective: "Desarrollar habilidades comercializables y comenzar a generar tus propios ingresos. El objetivo es crear valor por el que otros pagarán.",
        insight: "Aquí es donde todos comenzamos. No hay vergüenza en estar aquí — solo en quedarse aquí sin un plan."
      },
      2: {
        title: "Etapa 2: Productividad",
        explanation: "Ahora generas ingresos a través de tus habilidades y esfuerzo, pero tus ganancias no cubren completamente tus gastos. Estás trabajando, pero aún luchando para llegar a fin de mes.",
        objective: "Aumentar tus ingresos y/o reducir gastos hasta que tus ganancias superen consistentemente tus costos.",
        insight: "Muchas personas se quedan atrapadas aquí por años. La clave es encontrar formas de aumentar el valor que creas, no solo trabajar más horas."
      },
      3: {
        title: "Etapa 3: Independencia",
        explanation: "Tus ingresos ahora cubren tus gastos, y a veces puedes ahorrar dinero. Ya no dependes de otros, pero aún no has construido reservas significativas.",
        objective: "Construir hábitos de ahorro consistentes y crear un fondo de emergencia que te dé espacio para respirar.",
        insight: "Este es un punto de inflexión crítico. Desde aquí, tus decisiones determinan si subes o te quedas estancado."
      },
      4: {
        title: "Etapa 4: Estabilidad",
        explanation: "Generas ahorros de manera consistente y puede que hayas comenzado a obtener ingresos extra de esos ahorros a través de intereses o pequeñas inversiones.",
        objective: "Hacer crecer tus inversiones y comenzar a crear flujos de ingresos pasivos que no requieran tu tiempo directo.",
        insight: "En esta etapa, tu dinero comienza a trabajar para ti. El efecto compuesto empieza a acelerar tu progreso."
      },
      5: {
        title: "Etapa 5: Seguridad",
        explanation: "Los ingresos de tus inversiones ahora cubren tus gastos básicos. Tienes activos diversificados y sin deudas costosas. El trabajo se convierte en una elección, no una necesidad para sobrevivir.",
        objective: "Expandir tus ingresos por inversiones hasta que cubran no solo lo básico, sino todo tu estilo de vida deseado.",
        insight: "Has logrado lo que la mayoría solo sueña — verdadera seguridad financiera. Pero aún hay más escalera por subir."
      },
      6: {
        title: "Etapa 6: Libertad Financiera",
        explanation: "Tus ingresos por inversiones cubren completamente tu estilo de vida con margen de sobra. Ya no necesitas trabajar por dinero. Tu tiempo es verdaderamente tuyo.",
        objective: "Optimizar tu portafolio para sostenibilidad a largo plazo y comenzar a pensar en impacto más allá de la riqueza personal.",
        insight: "La libertad no se trata de tener dinero ilimitado — se trata de tener suficiente para que el dinero ya no dicte tus decisiones."
      },
      7: {
        title: "Etapa 7: Abundancia",
        explanation: "Tu riqueza supera con creces tus necesidades. El dinero ya no es una preocupación. Ahora te enfocas en legado, impacto y significado más allá de las finanzas personales.",
        objective: "Diseñar cómo tu riqueza creará un impacto positivo duradero para otros y las generaciones futuras.",
        insight: "La etapa final no se trata de más dinero — se trata de usar tus recursos para crear algo que te sobreviva."
      }
    },
    ladderRecap: {
      title: "La Escalera Completa de Riqueza",
      subtitle: "Has aprendido las 7 etapas. Aquí está el panorama completo:",
      insight: "La mayoría de las personas pasan toda su vida sin saber en qué etapa están. Estás a punto de descubrir exactamente dónde te encuentras.",
      button: "Encontrar Mi Etapa"
    },
    questionIntro: {
      title: "Ahora Encontremos Tu Etapa",
      description: "Responde tres preguntas rápidas honestamente. No hay respuestas correctas o incorrectas — solo tu realidad actual.",
      button: "Comenzar Evaluación"
    },
    analyzing: {
      phrases: [
        'Evaluando autonomía...',
        'Midiendo resiliencia...',
        'Calculando trayectoria...',
        'Posicionando en la Escalera ProsperMind...'
      ]
    },
    result: {
      title: "¡Tu etapa en la escalera está lista!",
      currentStage: "Tu Etapa Actual",
      nextStage: "Siguiente Etapa",
      direction: "Tu Dirección",
      directionLabels: {
        advancing: "Avanzando",
        steady: "Estable",
        contracting: "Cayendo"
      },
      retakeButton: "Repetir Evaluación",
      finishButton: "Finalizar"
    }
  }
};

// =============================================================================
// LADDER VISUALIZATION COMPONENT
// =============================================================================
const LadderVisualization = ({ highlightedStage, userStage, showUserMarker = false, direction = null, lang = 'en' }) => {
  const stages = [...WEALTH_STAGES].reverse(); // Top to bottom: Abundance to Dependence
  
  // Helper to get translated stage name
  const getStageName = (stage) => {
    return lang === 'es' ? stage.ladderNameEs : stage.ladderName;
  };
  
  // Helper to get translated user marker text
  const getYouLabel = () => lang === 'es' ? 'TÚ' : 'YOU';
  
  return (
    <div className="relative w-full max-w-xs mx-auto">
      {/* Ladder rungs - compact */}
      <div className="space-y-1">
        {stages.map((stage, index) => {
          const isHighlighted = highlightedStage === stage.level;
          const isUserStage = userStage === stage.level;
          const Icon = stage.icon;
          
          return (
            <motion.div
              key={stage.key}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`
                relative flex items-center gap-2 p-1.5 rounded-lg transition-all duration-300
                ${isHighlighted ? 'ring-2 ring-white scale-105' : ''}
                ${isUserStage && showUserMarker ? 'ring-2 ring-yellow-400' : ''}
              `}
              style={{ 
                backgroundColor: isHighlighted || isUserStage ? `${stage.color}` : `${stage.color}33`,
                opacity: isHighlighted || isUserStage ? 1 : 0.6
              }}
            >
              {/* Stage icon */}
              <div className={`
                w-6 h-6 rounded-full flex items-center justify-center
                ${isHighlighted || isUserStage ? 'bg-white/20' : 'bg-white/10'}
              `}>
                <Icon className="w-3 h-3 text-white" />
              </div>
              
              {/* Stage name */}
              <div className="flex-1">
                <span className={`
                  text-xs font-medium
                  ${isHighlighted || isUserStage ? 'text-white' : 'text-white/70'}
                `}>
                  {getStageName(stage)}
                </span>
              </div>
              
              {/* User marker */}
              {isUserStage && showUserMarker && (
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-yellow-400 text-black text-xs font-bold rounded">
                    {getYouLabel()}
                  </span>
                  {direction && (
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center`}
                      style={{ backgroundColor: DIRECTIONS[direction]?.color }}>
                      {direction === 'advancing' && <ArrowUp className="w-2.5 h-2.5 text-white" />}
                      {direction === 'steady' && <ArrowRight className="w-2.5 h-2.5 text-white" />}
                      {direction === 'contracting' && <ArrowDown className="w-2.5 h-2.5 text-white" />}
                    </div>
                  )}
                </div>
              )}
              
              {/* Highlight indicator */}
              {isHighlighted && !showUserMarker && (
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </motion.div>
          );
        })}
      </div>
      
      {/* Vertical progression arrow */}
      <div className="absolute -right-6 top-0 bottom-0 flex flex-col items-center justify-center">
        <div className="w-0.5 h-full bg-gradient-to-t from-red-500 via-yellow-500 to-purple-500 opacity-30" />
        <ArrowUp className="absolute top-0 w-3 h-3 text-purple-400" />
      </div>
    </div>
  );
};

// =============================================================================
// MAIN MODULE COMPONENT
// =============================================================================
const WealthLadderEducationalModule = ({ onComplete, onClose, existingResult }) => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = content[lang];
  
  // Screen flow management
  const [currentScreen, setCurrentScreen] = useState(0);
  const [currentStageExplainer, setCurrentStageExplainer] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisPhrase, setAnalysisPhrase] = useState(0);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [taskAddedSuccess, setTaskAddedSuccess] = useState(false);
  
  // Convert existing result to proper format if needed
  const normalizeResult = (res) => {
    if (!res) return null;
    
    // If already has stage object with level property, return as-is
    if (res.stage && res.stage.level) return res;
    
    // Convert from stored format (stage_key, direction string) to full objects
    const stageKey = res.stage_key || res.stage?.key;
    const directionKey = res.direction_key || res.direction;
    
    if (!stageKey) return null;
    
    const stage = WEALTH_STAGES.find(s => s.key === stageKey);
    const direction = DIRECTIONS[directionKey] || DIRECTIONS.steady;
    
    if (!stage) return null;
    
    return {
      stage,
      direction,
      answers: res.answers || [],
      stage_key: stageKey,
      stage_name: stage.name,
      stage_level: stage.level,
      direction_key: direction.key
    };
  };
  
  const [result, setResult] = useState(() => normalizeResult(existingResult));
  
  // Track if user has existing result (for showing choice on intro screen)
  const hasExistingResult = Boolean(existingResult && normalizeResult(existingResult));
  
  // Do NOT auto-jump to result - always start at intro (screen 0)
  // User can choose to view result or start learning journey
  
  // Screen definitions
  // 0: Intro
  // 1: Concept
  // 2: Stage intro
  // 3-9: Stage explanations (7 stages)
  // 10: Ladder recap (shows full ladder before quiz)
  // 11: Question intro
  // 12: Question 1
  // 13: Stage insight after Q1
  // 14: Question 2
  // 15: Stage insight after Q2
  // 16: Question 3
  // 17: Analyzing
  // 100: Result summary
  // 101: Result with ladder visualization
  // 102: What's Next - Section 1 (Your Gap)
  // 103: What's Next - Section 2 (What's Blocking You)
  // 104: What's Next - Section 3 (Your Next Move + Action Item)
  
  const totalScreens = 19;
  
  // Analysis animation
  useEffect(() => {
    if (currentScreen === 17) {
      const phraseInterval = setInterval(() => {
        setAnalysisPhrase(prev => (prev + 1) % c.analyzing.phrases.length);
      }, 1200);

      const progressInterval = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 100) return 100;
          const increment = Math.max(1, 15 - (prev / 10));
          return Math.min(100, prev + increment);
        });
      }, 100);

      const timeout = setTimeout(() => {
        const [q1, q2, q3] = answers;
        const stageKey = calculateStage(q1, q2, q3);
        const directionKey = calculateDirection(q2, q3);
        const stage = WEALTH_STAGES.find(s => s.key === stageKey);
        const direction = DIRECTIONS[directionKey];
        
        const calculatedResult = {
          stage,
          direction,
          answers,
          stage_key: stage.key,
          stage_name: stage.name,
          stage_level: stage.level,
          direction_key: direction.key
        };
        
        setResult(calculatedResult);
        setCurrentScreen(100);
        
        // Do NOT call onComplete here - let user see results first
        // onComplete will be called when user clicks "Continue to Dashboard" button
      }, 5000);

      return () => {
        clearInterval(phraseInterval);
        clearInterval(progressInterval);
        clearTimeout(timeout);
      };
    }
  }, [currentScreen, answers, onComplete, c.analyzing.phrases.length]);
  
  const handleAnswer = (option) => {
    setSelectedOption(option.id);
    
    setTimeout(() => {
      const newAnswers = [...answers, option.value];
      setAnswers(newAnswers);
      setSelectedOption(null);
      
      // Move to next screen based on which question was answered
      if (currentQuestion === 0) {
        setCurrentQuestion(1);
        setCurrentScreen(13); // Insight after Q1
      } else if (currentQuestion === 1) {
        setCurrentQuestion(2);
        setCurrentScreen(15); // Insight after Q2
      } else {
        setCurrentScreen(17); // Analyzing
      }
    }, 600);
  };
  
  const handleRetake = () => {
    setAnswers([]);
    setCurrentQuestion(0);
    setResult(null);
    setAnalysisProgress(0);
    setCurrentScreen(11); // Back to question intro
  };
  
  const handleBackQuestion = () => {
    // Handle going back in the quiz
    if (currentScreen === 12) {
      // On Question 1, go back to question intro
      setCurrentScreen(11);
    } else if (currentScreen === 13) {
      // On insight after Q1, go back to Q1
      setAnswers(prev => prev.slice(0, -1)); // Remove last answer
      setCurrentQuestion(0);
      setCurrentScreen(12);
    } else if (currentScreen === 14) {
      // On Question 2, go back to insight after Q1
      setCurrentScreen(13);
    } else if (currentScreen === 15) {
      // On insight after Q2, go back to Q2
      setAnswers(prev => prev.slice(0, -1)); // Remove last answer
      setCurrentQuestion(1);
      setCurrentScreen(14);
    } else if (currentScreen === 16) {
      // On Question 3, go back to insight after Q2
      setCurrentScreen(15);
    }
  };
  
  const nextScreen = () => {
    if (currentScreen < 9 && currentScreen >= 3) {
      // We're in stage explanations
      setCurrentStageExplainer(prev => prev + 1);
    }
    setCurrentScreen(prev => prev + 1);
  };
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };
  
  // Calculate progress percentage
  const getProgress = () => {
    if (currentScreen === 100) return 100;
    return Math.min(100, (currentScreen / totalScreens) * 100);
  };
  
  // Helper to go back to previous screen
  const goBack = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  };
  
  // Handler for completing quiz and going to dashboard
  const handleFinishAndGoToDashboard = () => {
    if (result && onComplete) {
      onComplete({
        stage_key: result.stage.key,
        stage_name: result.stage.name,
        stage_level: result.stage.level,
        direction: result.direction.key,
        answers: result.answers
      });
    }
    onClose();
  };
  
  // Handler for adding the recommended action as a task
  const handleAddTask = async () => {
    if (!result) return;
    
    const API = process.env.REACT_APP_BACKEND_URL;
    const stageKey = result.stage.key;
    
    // Get tasks for this stage from translations
    const tasks = t(`whatsNext.section3.tasks.${stageKey}`, { returnObjects: true }) || [];
    
    if (!Array.isArray(tasks) || tasks.length === 0) {
      console.error('No tasks found for stage:', stageKey);
      alert(t('whatsNext.section3.taskAddError'));
      return;
    }
    
    setIsAddingTask(true);
    try {
      // Get auth token
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Create all tasks for this stage
      for (let i = 0; i < tasks.length; i++) {
        const taskText = tasks[i];
        
        // Create a due date: first task 7 days, subsequent tasks add 7 more days each
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 7 + (i * 7));
        
        // First task is "the_one_thing" (high priority), others are "income_growth" (medium priority)
        const category = i === 0 ? 'the_one_thing' : 'income_growth';
        const priority = i === 0 ? 'high' : 'medium';
        
        await axios.post(`${API}/api/tasks`, {
          title: taskText,
          description: t('whatsNext.title'),
          category: category,
          due_date: dueDate.toISOString(),
          priority: priority,
          estimated_impact: 0
        }, { headers });
      }
      
      setTaskAddedSuccess(true);
      // Wait a moment to show success, then proceed
      setTimeout(() => {
        handleFinishAndGoToDashboard();
      }, 1500);
    } catch (error) {
      console.error('Error adding task:', error);
      alert(t('whatsNext.section3.taskAddError'));
      setIsAddingTask(false);
    }
  };
  
  // Helper to render back button for educational slides
  const BackButton = ({ onBack }) => (
    <div className="flex items-center justify-start px-3 md:px-6 pt-4 pb-2">
      <button
        onClick={onBack || goBack}
        className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium"
      >
        <ChevronLeft className="w-5 h-5" />
        {lang === 'es' ? 'Atrás' : 'Back'}
      </button>
    </div>
  );
  
  // ==========================================================================
  // RENDER FUNCTIONS
  // ==========================================================================
  
  const renderIntro = () => (
    <motion.div 
      key="intro"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Content area - grows to fill space */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-3 md:px-6">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl flex items-center justify-center mb-2 md:mb-4">
          <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-black" />
        </div>
        
        <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-white mb-1 md:mb-2">{c.intro.title}</h1>
        <p className="text-base md:text-xl text-yellow-400 mb-2 md:mb-4">{c.intro.subtitle}</p>
        <p className="text-gray-400 max-w-xs md:max-w-md lg:max-w-lg text-sm md:text-base lg:text-lg leading-relaxed">{c.intro.description}</p>
        
        <p className="text-gray-500 text-xs md:text-sm mt-2 md:mt-4">{c.intro.time}</p>
      </div>
      
      {/* Button area - fixed at bottom with extra space for iPhone safe area */}
      <div className="flex-shrink-0 flex flex-col items-center gap-2 md:gap-3 pb-44 md:pb-20">
        <button
          onClick={nextScreen}
          data-testid="start-wealth-journey-btn"
          className="px-5 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
        >
          {c.intro.button}
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
        
        {hasExistingResult && (
          <button
            onClick={() => setCurrentScreen(100)}
            data-testid="view-existing-result-btn"
            className="px-4 py-1.5 md:px-6 md:py-2 bg-gray-800 text-gray-300 font-medium rounded-xl hover:bg-gray-700 transition-all flex items-center gap-2 text-xs md:text-sm"
          >
            {lang === 'es' ? 'Ver mi resultado actual' : 'View my current result'}
            <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
  
  const renderConcept = () => (
    <motion.div 
      key="concept"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Back button */}
      <BackButton />
      
      {/* Content area */}
      <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6">
        <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-2 md:mb-4">
          <BookOpen className="w-5 h-5 md:w-7 md:h-7 text-white" />
        </div>
        
        <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 md:mb-3 text-center">{c.concept.title}</h1>
        
        <p className="text-gray-300 mb-3 md:mb-4 text-center max-w-sm md:max-w-lg text-sm md:text-base lg:text-lg leading-relaxed">{c.concept.intro}</p>
        
        {/* Compact visual explanation */}
        <div className="w-full max-w-xs md:max-w-md lg:max-w-lg bg-gray-800/50 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="w-6 h-6 md:w-8 md:h-8 rounded bg-red-600 flex items-center justify-center">
                <ArrowDown className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
              <span className="text-red-400 text-xs md:text-sm font-medium">
                {lang === 'es' ? 'Menos libertad' : 'Less freedom'}
              </span>
            </div>
          </div>
          
          <div className="h-2 md:h-3 rounded-full bg-gradient-to-r from-red-600 via-yellow-500 to-purple-600 mb-2 md:mb-3" />
          
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-1.5 md:gap-2">
              <span className="text-purple-400 text-xs md:text-sm font-medium">
                {lang === 'es' ? 'Más libertad' : 'More freedom'}
              </span>
              <div className="w-6 h-6 md:w-8 md:h-8 rounded bg-purple-600 flex items-center justify-center">
                <ArrowUp className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            </div>
          </div>
          
          <p className="text-gray-400 text-xs md:text-sm text-center mt-2 md:mt-3">
            {lang === 'es' 
              ? '7 etapas desde Dependencia hasta Abundancia' 
              : '7 stages from Dependence to Abundance'}
          </p>
        </div>
        
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-1.5 md:px-4 md:py-2 max-w-xs md:max-w-md">
          <p className="text-yellow-400 text-xs md:text-sm">💡 {c.concept.insight}</p>
        </div>
      </div>
      
      {/* Button area - fixed at bottom with extra space for iPhone safe area */}
      <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
        >
          {c.concept.button}
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>
    </motion.div>
  );
  
  const renderStageIntro = () => (
    <motion.div 
      key="stageIntro"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Back button */}
      <BackButton />
      
      {/* Content area */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-3 md:px-6">
        <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-2 md:mb-4">
          <Target className="w-5 h-5 md:w-7 md:h-7 text-white" />
        </div>
        
        <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 md:mb-3">{c.stageIntro.title}</h1>
        <p className="text-gray-400 max-w-xs md:max-w-md lg:max-w-lg text-sm md:text-base lg:text-lg leading-relaxed">{c.stageIntro.description}</p>
      </div>
      
      {/* Button area - fixed at bottom */}
      <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
        <button
          onClick={nextScreen}
          className="px-5 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
        >
          {c.stageIntro.button}
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>
    </motion.div>
  );
  
  const renderStageExplainer = (stageNum) => {
    const stage = WEALTH_STAGES.find(s => s.level === stageNum);
    const stageContent = c.stages[stageNum];
    const Icon = stage.icon;
    
    return (
      <motion.div 
        key={`stage-${stageNum}`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Back button */}
        <BackButton />
        
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6">
          {/* Stage number indicator */}
          <div className="text-gray-500 text-xs md:text-sm mb-1 md:mb-2">
            {lang === 'es' ? `Etapa ${stageNum} de 7` : `Stage ${stageNum} of 7`}
          </div>
          
          {/* Single Stage Card - responsive */}
          <div 
            className="w-full max-w-sm md:max-w-lg lg:max-w-xl mb-2 md:mb-4 rounded-lg p-3 md:p-5 lg:p-6 shadow-lg border-2"
            style={{ 
              backgroundColor: `${stage.color}15`,
              borderColor: stage.color 
            }}
          >
            {/* Stage header */}
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <div 
                className="w-8 h-8 md:w-12 md:h-12 rounded-lg flex items-center justify-center shadow-lg"
                style={{ backgroundColor: stage.color }}
              >
                <Icon className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
              <div>
                <p className="text-xs md:text-sm uppercase tracking-wider" style={{ color: stage.color }}>
                  {lang === 'es' ? stage.nameEs : stage.name}
                </p>
                <h1 className="text-base md:text-xl lg:text-2xl font-bold text-white">
                  {lang === 'es' ? stage.ladderNameEs : stage.ladderName}
                </h1>
              </div>
            </div>
            
            {/* Explanation */}
            <p className="text-gray-300 text-sm md:text-base lg:text-lg mb-2 md:mb-4 leading-relaxed">{stageContent.explanation}</p>
            
            {/* Objective */}
            <div className="bg-gray-900/50 rounded-lg p-2 md:p-3 border-l-4" style={{ borderColor: stage.color }}>
              <p className="text-xs md:text-sm text-gray-400 uppercase mb-0.5 md:mb-1">{lang === 'es' ? 'Objetivo' : 'Objective'}</p>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">{stageContent.objective}</p>
            </div>
          </div>
          
          {/* Insight */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-1.5 md:px-4 md:py-2 max-w-sm md:max-w-lg w-full">
            <p className="text-yellow-400 text-xs md:text-sm">💡 {stageContent.insight}</p>
          </div>
        </div>
        
        {/* Button area - fixed at bottom */}
        <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
          <button
            onClick={nextScreen}
            className="px-4 py-2 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
          >
            {stageNum < 7 ? (lang === 'es' ? 'Siguiente Etapa' : 'Next Stage') : (lang === 'es' ? 'Continuar' : 'Continue')}
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </motion.div>
    );
  };
  
  // Render the ladder recap screen with the original LadderOfWealthChart
  const renderLadderRecap = () => {
    return (
      <motion.div 
        key="ladderRecap"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Back button */}
        <BackButton />
        
        {/* Content area - allow more space for the chart */}
        <div className="flex-1 flex flex-col items-center px-3 md:px-6 overflow-hidden">
          <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-0.5 text-center flex-shrink-0">{c.ladderRecap.title}</h1>
          <p className="text-gray-400 mb-1 text-center max-w-md md:max-w-lg text-xs md:text-sm flex-shrink-0">{c.ladderRecap.subtitle}</p>
          
          {/* Insight box - moved here between subtitle and ladder */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-2 py-0.5 md:px-3 md:py-1 max-w-md md:max-w-lg mb-1 flex-shrink-0">
            <p className="text-yellow-400 text-xs">💡 {c.ladderRecap.insight}</p>
          </div>
          
          {/* Original Ladder of Wealth Chart - give it more vertical space */}
          <div className="w-full max-w-4xl lg:max-w-5xl mx-auto flex-1 min-h-0">
            <LadderOfWealthChart 
              quizResult={null}
              hideTitle={true}
              compact={false}
              hideInfoBox={true}
            />
          </div>
        </div>
        
        {/* Button area - fixed at bottom with less padding on desktop */}
        <div className="flex-shrink-0 flex justify-center pb-44 md:pb-16 pt-2">
          <button
            onClick={nextScreen}
            data-testid="find-my-stage-btn"
            className="px-5 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
          >
            {c.ladderRecap.button}
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </motion.div>
    );
  };
  
  const renderQuestionIntro = () => (
    <motion.div 
      key="questionIntro"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Back button */}
      <BackButton />
      
      {/* Content area */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-3 md:px-6">
        <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-2 md:mb-4">
          <Zap className="w-5 h-5 md:w-7 md:h-7 text-white" />
        </div>
        
        <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 md:mb-3">{c.questionIntro.title}</h1>
        <p className="text-gray-400 max-w-xs md:max-w-md lg:max-w-lg text-sm md:text-base lg:text-lg leading-relaxed">{c.questionIntro.description}</p>
      </div>
      
      {/* Button area - fixed at bottom */}
      <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
        <button
          onClick={() => setCurrentScreen(12)}
          className="px-5 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-2 text-sm md:text-base"
        >
          {c.questionIntro.button}
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>
    </motion.div>
  );
  
  const renderQuestion = (questionIndex) => {
    const question = QUESTIONS[questionIndex];
    
    return (
      <motion.div 
        key={`question-${questionIndex}`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Header with Back button */}
        <div className="flex items-center justify-between px-3 md:px-6 pt-4">
          <button
            onClick={handleBackQuestion}
            className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            {lang === 'es' ? 'Atrás' : 'Back'}
          </button>
          <div className="text-gray-500 text-xs md:text-sm">
            {lang === 'es' ? 'Pregunta' : 'Question'} {questionIndex + 1} / 3
          </div>
        </div>
        
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6">
          <h2 className="text-base md:text-xl lg:text-2xl font-bold text-white mb-3 md:mb-6 text-center max-w-sm md:max-w-lg lg:max-w-xl leading-relaxed">
            {lang === 'es' ? question.questionEs : question.question}
          </h2>
          
          <div className="grid gap-1.5 md:gap-3 w-full max-w-sm md:max-w-lg lg:max-w-xl">
            {question.options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleAnswer(option)}
                disabled={selectedOption !== null}
                className={`
                  p-2.5 md:p-4 rounded-lg text-left transition-all
                  ${selectedOption === option.id 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-800/50 border border-gray-700 hover:border-yellow-500/50 hover:bg-gray-800 text-white'}
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm md:text-base lg:text-lg leading-relaxed">{lang === 'es' ? option.labelEs : option.label}</span>
                  {selectedOption === option.id && <Check className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ml-2" />}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Empty button area for consistent spacing */}
        <div className="flex-shrink-0 h-16 pb-44 md:pb-20"></div>
      </motion.div>
    );
  };
  
  const renderInsightSlide = (afterQuestion) => {
    // Show a relevant insight based on the last answer
    const lastAnswer = answers[answers.length - 1];
    let insightStage = 3; // Default to Independence
    
    if (afterQuestion === 1) {
      // After Q1 - show stage based on answer
      if (lastAnswer === 1) insightStage = 1;
      else if (lastAnswer === 2) insightStage = 3;
      else if (lastAnswer === 3) insightStage = 5;
      else if (lastAnswer === 4) insightStage = 6;
      else insightStage = 7;
    } else if (afterQuestion === 2) {
      // After Q2 - show stability/security insight
      if (lastAnswer <= 2) insightStage = 2;
      else insightStage = 4;
    }
    
    const stage = WEALTH_STAGES.find(s => s.level === insightStage);
    const stageContent = c.stages[insightStage];
    
    return (
      <motion.div 
        key={`insight-${afterQuestion}`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Header with Back button */}
        <div className="flex items-center justify-between px-3 md:px-6 pt-4">
          <button
            onClick={handleBackQuestion}
            className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            {lang === 'es' ? 'Atrás' : 'Back'}
          </button>
          <div className="text-gray-500 text-xs md:text-sm">
            {lang === 'es' ? 'Reflexión' : 'Reflection'}
          </div>
        </div>
        
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6">
          <h2 className="text-sm md:text-lg lg:text-xl font-semibold text-white mb-2 md:mb-4 text-center">
            {lang === 'es' ? 'Tu respuesta sugiere...' : 'Your answer suggests...'}
          </h2>
          
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 md:p-5 max-w-sm md:max-w-lg w-full">
            <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
              <div 
                className="w-6 h-6 md:w-10 md:h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: stage.color }}
              >
                {React.createElement(stage.icon, { className: "w-3 h-3 md:w-5 md:h-5 text-white" })}
              </div>
              <span className="text-white font-medium text-sm md:text-lg">{stageContent.title}</span>
            </div>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed">{stageContent.explanation}</p>
          </div>
        </div>
        
        {/* Button area - fixed at bottom */}
        <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
          <button
            onClick={() => setCurrentScreen(afterQuestion === 1 ? 14 : 16)}
            className="px-4 py-2 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2 text-sm md:text-base"
          >
            {lang === 'es' ? 'Siguiente Pregunta' : 'Next Question'}
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </motion.div>
    );
  };
  
  const renderAnalyzing = () => (
    <motion.div 
      key="analyzing"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Content area */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4 md:px-6">
        <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-yellow-400 animate-pulse" />
        </div>
        
        <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-white mb-4 md:mb-6">
          {c.analyzing.phrases[analysisPhrase]}
        </h2>
        
        <div className="w-full max-w-xs md:max-w-md bg-gray-800 rounded-full h-2 md:h-3 overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-yellow-500 to-amber-500"
            style={{ width: `${analysisProgress}%` }}
          />
        </div>
      </div>
      
      {/* Empty button area for consistent spacing */}
      <div className="flex-shrink-0 h-16 pb-44 md:pb-20"></div>
    </motion.div>
  );
  
  const renderResult = () => {
    if (!result) return null;
    
    const stage = result.stage;
    const direction = result.direction;
    const nextStage = WEALTH_STAGES.find(s => s.level === stage.level + 1);
    const DirectionIcon = direction.icon;
    
    return (
      <motion.div 
        key="result"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6 overflow-hidden">
          <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 md:mb-4 text-center">{c.result.title}</h1>
          
          {/* Ladder with user position - responsive scale */}
          <div className="mb-2 md:mb-4 transform scale-75 md:scale-90 lg:scale-100 origin-top">
            <LadderVisualization 
              highlightedStage={null} 
              userStage={stage.level} 
              showUserMarker={true}
              direction={direction.key}
              lang={lang}
            />
          </div>
          
          {/* Current stage info - responsive */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 md:p-4 max-w-sm md:max-w-lg w-full mb-2 md:mb-4">
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <div 
                className="w-6 h-6 md:w-10 md:h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: stage.color }}
              >
                {React.createElement(stage.icon, { className: "w-3 h-3 md:w-5 md:h-5 text-white" })}
              </div>
              <div>
                <p className="text-gray-400 text-xs md:text-sm uppercase">{c.result.currentStage}</p>
                <p className="text-white font-bold text-sm md:text-lg">{lang === 'es' ? stage.ladderNameEs : stage.ladderName}</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed">{lang === 'es' ? stage.descriptionEs : stage.description}</p>
          </div>
          
          {/* Direction and Next stage row */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center">
            <div className="flex items-center gap-1.5 md:gap-2 px-2 py-1 md:px-3 md:py-1.5 rounded-full" style={{ backgroundColor: `${direction.color}20` }}>
              <DirectionIcon className="w-3 h-3 md:w-4 md:h-4" style={{ color: direction.color }} />
              <span className="font-medium text-xs md:text-sm" style={{ color: direction.color }}>
                {c.result.directionLabels[direction.key]}
              </span>
            </div>
            
            {nextStage && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-full px-2 py-1 md:px-3 md:py-1.5">
                <span className="text-gray-400 text-xs md:text-sm">{c.result.nextStage}: </span>
                <span className="text-white font-medium text-xs md:text-sm">{lang === 'es' ? nextStage.ladderNameEs : nextStage.ladderName}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Button area - fixed at bottom */}
        <div className="flex-shrink-0 flex flex-wrap justify-center gap-1.5 md:gap-3 pb-44 md:pb-20">
          <button
            onClick={() => setCurrentScreen(101)}
            data-testid="view-on-ladder-btn"
            className="px-3 py-1.5 md:px-5 md:py-2.5 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm"
          >
            <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
            {lang === 'es' ? 'Ver Escalera' : 'View Ladder'}
          </button>
          
          <button
            onClick={() => setCurrentScreen(1)}
            data-testid="learn-stages-btn"
            className="px-3 py-1.5 md:px-5 md:py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm"
          >
            <BookOpen className="w-3 h-3 md:w-4 md:h-4" />
            {lang === 'es' ? 'Aprender' : 'Learn'}
          </button>
          
          <button
            onClick={handleRetake}
            data-testid="retake-quiz-btn"
            className="px-3 py-1.5 md:px-5 md:py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm"
          >
            <RotateCcw className="w-3 h-3 md:w-4 md:h-4" />
            {lang === 'es' ? 'Repetir' : 'Retake'}
          </button>
          
          <button
            onClick={() => setCurrentScreen(102)}
            data-testid="continue-dashboard-btn"
            className="px-3 py-1.5 md:px-5 md:py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm"
          >
            <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
            {lang === 'es' ? 'Continuar' : 'Continue'}
          </button>
        </div>
      </motion.div>
    );
  };
  
  // Render the ladder result screen with full ladder visualization
  const renderLadderResult = () => {
    if (!result) return null;
    
    const stage = result.stage;
    const direction = result.direction;
    
    return (
      <motion.div 
        key="ladderResult"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        {/* Content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-3 md:px-6 overflow-hidden">
          <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-1 md:mb-2 text-center">
            {lang === 'es' ? 'Tu Posición en la Escalera' : 'Your Position on the Ladder'}
          </h1>
          <p className="text-gray-400 mb-2 md:mb-4 text-center text-sm md:text-base">
            {lang === 'es' 
              ? `Estás en ${stage.ladderNameEs} y tu dirección es ${c.result.directionLabels[direction.key].toLowerCase()}`
              : `You are at ${stage.ladderName} and your direction is ${c.result.directionLabels[direction.key].toLowerCase()}`
            }
          </p>
          
          {/* Full Ladder Visualization with user's position - constrained height */}
          <div className="w-full max-w-3xl lg:max-w-4xl mx-auto flex-shrink-0" style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'hidden' }}>
            <LadderOfWealthChart 
              quizStageLevel={stage.level}
              quizDirection={direction.key}
              hideTitle={true}
              compact={true}
              focusStage={stage.level}
              diagnosisCompleted={true}
            />
          </div>
        </div>
        
        {/* Button area - fixed at bottom */}
        <div className="flex-shrink-0 flex justify-center gap-2 md:gap-3 pb-44 md:pb-20">
          <button
            onClick={() => setCurrentScreen(100)}
            className="px-4 py-2 md:px-6 md:py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all flex items-center gap-1.5 md:gap-2 text-sm md:text-base"
          >
            {lang === 'es' ? 'Ver Resumen' : 'View Summary'}
          </button>
          
          <button
            onClick={() => setCurrentScreen(102)}
            data-testid="finish-ladder-btn"
            className="px-5 py-2 md:px-8 md:py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-1.5 md:gap-2 text-sm md:text-base"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            {t('whatsNext.next')}
          </button>
        </div>
      </motion.div>
    );
  };
  
  // ==========================================================================
  // WHAT'S NEXT SCREENS
  // ==========================================================================
  
  // Get next stage for current user
  const getNextStage = () => {
    if (!result) return null;
    return WEALTH_STAGES.find(s => s.level === result.stage.level + 1);
  };
  
  // Section 1: Your Gap
  const renderWhatsNextGap = () => {
    if (!result) return null;
    
    const nextStage = getNextStage();
    const stageKey = result.stage.key;
    const gapContent = t(`whatsNext.section1.gap.${stageKey}`, { returnObjects: true });
    
    return (
      <motion.div 
        key="whatsNextGap"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 overflow-hidden">
          {/* Title */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-xl md:text-3xl font-bold text-white mb-2">
              {t('whatsNext.section1.title')} {nextStage ? (lang === 'es' ? nextStage.ladderNameEs : nextStage.ladderName) : ''}
            </h1>
          </div>
          
          {/* Gap comparison */}
          <div className="w-full max-w-lg">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {/* Current State */}
              <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: result.stage.color }}
                  >
                    {React.createElement(result.stage.icon, { className: "w-4 h-4 text-white" })}
                  </div>
                  <span className="text-gray-400 text-sm font-medium">{t('whatsNext.section1.now')}</span>
                </div>
                <p className="text-white text-sm md:text-base leading-relaxed">
                  {gapContent?.current || ''}
                </p>
              </div>
              
              {/* Next State */}
              <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 border border-green-700/50 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-2 mb-3">
                  {nextStage && (
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: nextStage.color }}
                    >
                      {React.createElement(nextStage.icon, { className: "w-4 h-4 text-white" })}
                    </div>
                  )}
                  <span className="text-green-400 text-sm font-medium">{t('whatsNext.section1.nextLevel')}</span>
                </div>
                <p className="text-white text-sm md:text-base leading-relaxed">
                  {gapContent?.next || ''}
                </p>
              </div>
            </div>
          </div>
          
          {/* Arrow indicator */}
          <div className="mt-6 text-gray-500">
            <ArrowRight className="w-8 h-8" />
          </div>
        </div>
        
        {/* Button */}
        <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
          <button
            onClick={() => setCurrentScreen(103)}
            className="px-6 py-3 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2"
          >
            {t('whatsNext.next')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    );
  };
  
  // Section 2: What's Blocking You
  const renderWhatsNextBlockers = () => {
    if (!result) return null;
    
    const stageKey = result.stage.key;
    const blockers = t(`whatsNext.section2.blockers.${stageKey}`, { returnObjects: true }) || [];
    
    return (
      <motion.div 
        key="whatsNextBlockers"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 overflow-hidden">
          {/* Title */}
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-500/20 mb-4">
              <AlertTriangle className="w-6 h-6 md:w-8 md:h-8 text-orange-400" />
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-white">
              {t('whatsNext.section2.title')}
            </h1>
          </div>
          
          {/* Blockers list */}
          <div className="w-full max-w-md space-y-3 md:space-y-4">
            {Array.isArray(blockers) && blockers.map((blocker, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.15 }}
                className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 flex items-start gap-3"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center mt-0.5">
                  <span className="text-orange-400 text-sm font-bold">{index + 1}</span>
                </div>
                <p className="text-gray-200 text-sm md:text-base">{blocker}</p>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Button */}
        <div className="flex-shrink-0 flex justify-center pb-44 md:pb-20">
          <button
            onClick={() => setCurrentScreen(104)}
            className="px-6 py-3 md:px-8 md:py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center gap-2"
          >
            {t('whatsNext.next')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    );
  };
  
  // Section 3: Your Next Move + Action Item
  const renderWhatsNextAction = () => {
    if (!result) return null;
    
    const stageKey = result.stage.key;
    const actionText = t(`whatsNext.section3.actions.${stageKey}`);
    
    return (
      <motion.div 
        key="whatsNextAction"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex flex-col h-full"
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 overflow-hidden">
          {/* Title */}
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-500/20 mb-4">
              <Lightbulb className="w-6 h-6 md:w-8 md:h-8 text-green-400" />
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-white">
              {t('whatsNext.section3.title')}
            </h1>
          </div>
          
          {/* Action card */}
          <div className="w-full max-w-md">
            <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 border-2 border-green-500/50 rounded-xl p-6 md:p-8 text-center">
              <Target className="w-10 h-10 md:w-12 md:h-12 text-green-400 mx-auto mb-4" />
              <p className="text-white text-lg md:text-xl font-medium leading-relaxed">
                "{actionText}"
              </p>
            </div>
            
            {/* Add task prompt */}
            <div className="mt-6 md:mt-8 text-center">
              {taskAddedSuccess ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center justify-center gap-2 text-green-400"
                >
                  <Check className="w-6 h-6" />
                  <span className="text-lg font-medium">
                    {(() => {
                      const tasks = t(`whatsNext.section3.tasks.${result?.stage?.key}`, { returnObjects: true }) || [];
                      return Array.isArray(tasks) && tasks.length > 1 
                        ? t('whatsNext.section3.tasksAdded') 
                        : t('whatsNext.section3.taskAdded');
                    })()}
                  </span>
                </motion.div>
              ) : (
                <>
                  <p className="text-gray-300 mb-4">{t('whatsNext.section3.addTaskPrompt')}</p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={handleAddTask}
                      disabled={isAddingTask}
                      className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      {isAddingTask ? (
                        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      {t('whatsNext.section3.addTask')}
                    </button>
                    <button
                      onClick={handleFinishAndGoToDashboard}
                      disabled={isAddingTask}
                      className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                    >
                      {t('whatsNext.section3.skip')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer spacer for consistent layout */}
        <div className="flex-shrink-0 pb-44 md:pb-20" />
      </motion.div>
    );
  };
  
  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  
  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 0: return renderIntro();
      case 1: return renderConcept();
      case 2: return renderStageIntro();
      case 3: return renderStageExplainer(1);
      case 4: return renderStageExplainer(2);
      case 5: return renderStageExplainer(3);
      case 6: return renderStageExplainer(4);
      case 7: return renderStageExplainer(5);
      case 8: return renderStageExplainer(6);
      case 9: return renderStageExplainer(7);
      case 10: return renderLadderRecap();
      case 11: return renderQuestionIntro();
      case 12: return renderQuestion(0);
      case 13: return renderInsightSlide(1);
      case 14: return renderQuestion(1);
      case 15: return renderInsightSlide(2);
      case 16: return renderQuestion(2);
      case 17: return renderAnalyzing();
      case 100: return renderResult();
      case 101: return renderLadderResult();
      case 102: return renderWhatsNextGap();
      case 103: return renderWhatsNextBlockers();
      case 104: return renderWhatsNextAction();
      default: return renderIntro();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">
      <div className="h-screen flex flex-col">
        {/* Progress bar - compact */}
        <div className="flex-shrink-0 bg-black border-b border-gray-800 px-4 py-2 md:py-3 z-10">
          <div className="max-w-xl md:max-w-2xl lg:max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs md:text-sm">
                {lang === 'es' ? 'Escalera de Riqueza' : 'Wealth Ladder'}
              </span>
              <span className="text-gray-500 text-xs md:text-sm">
                {currentScreen === 100 ? '100%' : `${Math.round(getProgress())}%`}
              </span>
            </div>
            <div className="h-1 md:h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-yellow-500 to-amber-500"
                initial={{ width: 0 }}
                animate={{ width: `${getProgress()}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-3 md:right-3 text-gray-500 hover:text-white transition-colors p-1.5 md:p-2 z-20"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Content - fixed height container for consistent button placement */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl h-full px-4 md:px-8">
              <AnimatePresence mode="wait">
                {renderCurrentScreen()}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WealthLadderEducationalModule;
